<template>
  <div>
    <account-loader :account-id="accountId" v-model="account" />
    <q-table
      v-if="isGranted('VIEW_USERS')"
      :columns="columns"
      :rows="users"
      v-model:pagination="pagination"
      :loading="isLoading"
      wrap-cells
      @request="pagination = $event.pagination"
    >
      <template v-slot:body-cell-role="props">
        <q-td :props="props">
          <role-tag v-if="props.value" :role="props.value" />
        </q-td>
      </template>
      <template v-slot:body-cell-clients="props">
        <q-td :props="props">
          <user-clients-btn :user="props.row" />
        </q-td>
      </template>
      <template v-slot:body-cell-status="props">
        <q-td :props="props">
          <user-status
            :user="props.row"
            @update:user="setUser"
          />
        </q-td>
      </template>
      <template v-slot:body-cell-createdAt="props">
        <q-td :props="props">
          <humanize-time :time="props.value" />
        </q-td>
      </template>
      <template v-slot:body-cell-actions="props">
        <q-td :props="props">
          <edit-user-btn :user="props.row" flat round @saved="set" />
          <delete-user-btn :user="props.row" class="on-right" flat round @deleted="remove" />
        </q-td>
      </template>
    </q-table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { HasTaskMixin } from '@/mixins';
import isGranted from '@/auth/isGranted';
import { DetailedUser, QTablePagination, User } from '@/type';
import eventBus from '@/messaging/event-bus';
import { UserAssignedToClient, UserUnassignedFromClient } from '@/messaging/events';
import HumanizeTime from '@/components/HumanizeTime.vue';
import UserClientsBtn from '@/user-client/UserClientsBtn.vue';
import AccountLoader from '@/iam/account/AccountLoader.vue';
import { AccountType } from '@/iam/account';
import { Account } from '@/iam/types';
import UsersMixin from './UsersMixin';
import UserStatus from './UserStatus.vue';
import RoleTag from '../role/RoleTag.vue';
import EditUserBtn from './EditUserBtn.vue';
import DeleteUserBtn from './DeleteUserBtn.vue';

interface Data {
  account: Account|null,
}

export default defineComponent({
  mixins: [UsersMixin, HasTaskMixin],
  components: {
    AccountLoader,
    RoleTag,
    UserStatus,
    HumanizeTime,
    EditUserBtn,
    DeleteUserBtn,
    UserClientsBtn,
  },
  data(): Data {
    return {
      account: null,
    };
  },
  computed: {
    isCustomer(): boolean {
      return this.account ? AccountType.isCustomer(this.account.type) : false;
    },
    columns(): object[] {
      const cols = [
        {
          name: 'name',
          label: 'Name',
          field: (user: DetailedUser) => `${user.firstName} ${user.lastName}`,
          align: 'left',
        },
        {
          name: 'email',
          label: 'Email',
          field: 'email',
          align: 'left',
        },
        {
          name: 'role',
          label: 'Role',
          field: 'role',
          align: 'center',
        },
      ];
      if (this.isCustomer) {
        cols.push({
          name: 'clients',
          label: 'Clients',
          field: 'clients',
          align: 'center',
        });
      }
      return [
        ...cols,
        {
          name: 'status',
          label: 'Status',
          field: 'status',
          align: 'center',
        },
        {
          name: 'createdAt',
          label: 'Created',
          field: 'createdAt',
          align: 'left',
        },
        {
          name: 'actions',
          label: '',
          align: 'right',
        },
      ];
    },
    pagination: {
      get(): QTablePagination {
        return {
          page: this.page,
          rowsPerPage: this.limit,
          rowsNumber: this.total,
        };
      },
      set(pagination: QTablePagination) {
        this.page = pagination.page;
        this.limit = pagination.rowsPerPage;
      },
    },
  },
  methods: {
    onAssignedToClient(event: UserAssignedToClient) {
      const { userClient } = event;
      const user = this.users.find(u => u.id === userClient.user.id);
      if (user) {
        user.totalSubscriptions += 1;
      }
    },
    onUnassignedFromClient(event: UserUnassignedFromClient) {
      const { userClient } = event;
      const user = this.users.find(u => u.id === userClient.user.id);
      if (user) {
        user.totalSubscriptions -= 1;
      }
    },
    setUser(user: User) {
      const i = this.users.findIndex(u => u.id === user.id);
      if (i >= 0) {
        this.users.splice(i, 1, { ...this.users[i], ...user });
      }
    },
    isGranted,
  },
  created(): void {
    eventBus.$on(UserAssignedToClient.eventName, this.onAssignedToClient);
    eventBus.$on(UserUnassignedFromClient.eventName, this.onUnassignedFromClient);
  },
  beforeUnmount(): void {
    eventBus.$off(UserAssignedToClient.eventName, this.onAssignedToClient);
    eventBus.$off(UserUnassignedFromClient.eventName, this.onUnassignedFromClient);
  },
});
</script>
